import { Children, useMemo, type ChangeEvent } from 'react';
import {
  useDayPicker,
  type DropdownNavProps,
  type DropdownProps,
  type NextMonthButtonProps,
  type PreviousMonthButtonProps,
} from 'react-day-picker';

import { ArrowLeft, ArrowRight } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { getMonth } from 'date-fns';

import { Button } from '../../lib/button.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/select.jsx';

export const SelectNavigation = (
  dropdownProps: DropdownProps & {
    container: HTMLDivElement | null;
    selectSize?: 'small' | 'medium';
  },
) => {
  const monthsInYear = 12;
  const isMonthDropdown = dropdownProps.options?.length === monthsInYear;
  const isSecondSelect = dropdownProps.displayIndex === 1;
  const options = isMonthDropdown
    ? dropdownProps.options
    : dropdownProps.options?.toReversed();
  const formattedOptions = useMemo(() => {
    return options?.map((option) => ({
      value: option.value,
      label: option.label.charAt(0).toUpperCase() + option.label.slice(1),
    }));
  }, [options]);

  const handleDropdownChange = (value: string) => {
    /** this logic helps us so that user can choose every month in every Select */
    const newValue =
      isMonthDropdown && isSecondSelect ? Number(value) - 1 : value;
    const event = {
      target: {
        value: newValue,
      },
    } as ChangeEvent<HTMLSelectElement>;
    dropdownProps.onChange?.(event);
  };

  return (
    <Select
      value={String(dropdownProps.value)}
      onValueChange={handleDropdownChange}
    >
      <SelectTrigger
        className={cn(dropdownProps.className)}
        size={dropdownProps.selectSize}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent container={dropdownProps.container} className="h-52">
        {formattedOptions?.map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const DropdownNavigationWrapper = (
  dropdownNavProps: DropdownNavProps,
) => {
  const ctx = useDayPicker();
  const data = Children.toArray(dropdownNavProps.children);
  const firstChild = data[0];
  const isFirstMonth =
    typeof firstChild === 'object' &&
    'props' in firstChild &&
    typeof firstChild.props === 'object' &&
    firstChild.props !== null &&
    'value' in firstChild.props &&
    typeof firstChild.props.value === 'number' &&
    getMonth(ctx.months[0].date) === firstChild.props.value;

  const padding = isFirstMonth && 'ml-13 md:ml-8';
  return (
    <div className={cn(padding, dropdownNavProps.className)}>
      {dropdownNavProps.children}
    </div>
  );
};

export const PreviousMonthButton = (props: PreviousMonthButtonProps) => {
  return (
    <Button
      data-testid="previous-month-button"
      variant="text"
      onClick={props.onClick}
      className={cn('ml-1', props.className)}
      icon
    >
      <ArrowLeft />
    </Button>
  );
};
export const NextMonthButton = (props: NextMonthButtonProps) => {
  return (
    <Button
      data-testid="next-month-button"
      variant="text"
      onClick={props.onClick}
      className={cn('mr-1', props.className)}
      icon
    >
      <ArrowRight />
    </Button>
  );
};
